/* eslint-disable no-restricted-globals */
import React, { memo, useEffect, useMemo, useContext } from "react";
import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  RawDraftContentState,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import styled from "styled-components";
import { sanitizeUrl } from "@braintree/sanitize-url";
import ScriptContext, { IScripts } from "context/ScriptContext";
import getCDNImage from "helpers/getCDNImage";
import { Loader } from "semantic-ui-react";

import { OEMBED_CONTENT_TYPE } from "../RichTextEditor/plugins/embed/constants";

interface RichTextDisplayProps {
  textJson: RawDraftContentState | null | undefined;
  id: string;
  limit?: number;
  loading?: boolean;
}
export const StyledRichTextHTML = styled.div(({ theme }) => ({
  "&&&": {
    // OVERALL
    margin: "1em 0px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
    textAlign: "initial", // overriding org page weird centering
    // maxWidth: "750px",
    // marginLeft: "auto",
    // marginRight: "auto",

    // FIGURES
    ".figure-center": {
      textAlign: "center",
      margin: "0 0 1em",
      ">*": {
        display: "block",
        maxWidth: "100%",
        marginLeft: "auto",
        marginRight: "auto",
      },
    },
    ".figure-left": {
      textAlign: "center",
      float: "none",
      margin: "0 0 1em",
      ">*": {
        maxWidth: "100%",
      },
    },
    ".figure-right": {
      textAlign: "center",
      float: "none",
      margin: "0 0 1em",
      ">*": {
        maxWidth: "100%",
      },
    },
    [`@media only screen and (min-width: 768px)`]: {
      ".figure-left": {
        width: "45%",
        float: "left",
        margin: "0.5em 1.5em 0.5em 0",
      },
      ".figure-right": {
        width: "45%",
        float: "right",
        margin: "0.5em 0 0.5em 1.5em",
      },
    },
    figure: {
      margin: 0,
      lineHeight: 0, // why?
      "& > *": {
        maxWidth: "100%",
      },
    },
    // CAPTION TEXT
    ".figcaption-text": {
      fontFamily: '"Raleway", sans-serif', // Roboto
      fontStyle: "normal",
      fontWeight: "normal",
      fontSize: "12px",
      lineHeight: "16px",
      color: "#757575",
      ...(theme?.typography?.captions || {}), // Overwriting with theme, if available.
      marginTop: "0.5em",
    },
    // STYLING FOR OLDER IFRAMES
    iframe: {
      marginLeft: "auto",
      marginRight: "auto",
      display: "block",
    },

    // TEXT
    "& h1, h2, h3, h4, h5, h6, p, li": {
      fontFamily: `${theme?.typography?.fontFamily || "Ubuntu"}`,
    },
    "& h4, p, li": {
      fontSize: `16px`,
    },

    // LINKS
    "& * a": {
      color: theme?.palette?.link?.default || "#5e93c5",
      textDecoration: "none",
    },
    "& * a:visited": {},
    "& * a:hover": {
      color: theme?.palette?.link?.hover || "#7eadda",
      outline: 0 /* reset for :focus */,
      cursor: "pointer",
    },
    "& * a:focus": {
      color: theme?.palette?.link?.focus || "#7eadda",
      outline: 0 /* reset for :focus */,
      cursor: "pointer",
    },
    "& * a:active": {
      color: theme?.palette?.link?.active || "#4a7bab",
    },

    // BLOCKQUOTES
    "& blockquote": {
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      color: "#1a1a1a",
      fontSize: "16px",
      fontStyle: "italic",
      lineHeight: "1.8em",
      margin: "1.1em 1em",
      padding: "1em 2em",
      position: "relative",
      transition: ".2s border ease-in-out",
      [`@media only screen and (max-width: 768px)`]: {
        paddingLeft: "1em",
        paddingRight: 0,
        marginLeft: 0,
        marginRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    "& blockquote:before": {
      content: "' '",
      position: "absolute",
      backgroundColor: "white",
      top: "50%",
      left: "-4px",
      height: "3em",
      width: "5px",
      marginTop: "-1.5em",
    },
    "& blockquote:after": {
      content: "'\\201C'",
      position: "absolute",
      top: "50%",
      left: "-0.5em",
      color: theme.palette.primary.main,
      fontStyle: "normal",
      lineHeight: "1.5em",
      fontSize: "4em",
      textAlign: "center",
      textIndent: "-2px",
      width: "1em",
      marginTop: "-0.5em",
      transition: ".2s all ease-in-out, .4s transform ease-in-out",
    },

    // EMBED TWEAKS
    ".embed": {
      outline: "none",
      width: "100%",
      height: "auto",
      display: "flex",
      justifyContent: "center",
      margin: "0 auto 1em",
      blockquote: {
        background: "none",
        borderLeft: "none",
        margin: "0",
        padding: "0",
      },
      "> :first-child": {
        flex: 1,
      },
    },
  },
}));

function isOldCSSSize(size: string) {
  if (typeof size !== "string") return false;
  if (!isNaN(size as any)) return false;
  if (size === "auto") return true;
  if (size.endsWith("px")) return true;
  if (size.endsWith("%")) return true;
  return false;
}

const customEntityTransform = (entity: any, child: string) => {
  switch (entity.type) {
    case "IMAGE":
    case "image": {
      const {
        data: { alignment, src, captions, width, height, alt },
      } = entity;

      const figureClass = `figure-${alignment || "default"}`;
      const preparedAlt = alt || "";
      const captionHTML = captions
        ? `<figcaption class="figcaption-text">${captions}</figcaption>`
        : "";
      let imageHTML = `
        <img
          src="${sanitizeUrl(src)}" 
          alt="${preparedAlt}"
          width="${isOldCSSSize(width) && width}"
          width="${isOldCSSSize(height) && height}"
          style="margin-left: auto; margin-right: auto; display: block;"
        ></img>`; // Default for older images without width and height

      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(width) && !isNaN(height)) {
        // Resizing image to the maximum allowed for blogs
        let adjustedWidth = width;
        let adjustedHeight = height;

        if (width > 700 && height) {
          const coefficient = 700 / width;
          adjustedWidth = 700;
          adjustedHeight = Math.floor(height * coefficient);
        }

        const aspect = (adjustedHeight / adjustedWidth) * 100;
        const preparedSrc = getCDNImage(
          sanitizeUrl(src),
          `${adjustedWidth}x${adjustedHeight}`,
        );

        // many divs are here to make sure that image does not jump in case it does not load and keeps aspect ratio
        imageHTML = `
            <div style="max-width: ${adjustedWidth}px; max-height: ${adjustedHeight}px;">
              <div
                style="position: relative; padding-bottom: ${aspect}%"
              >
                <img
                  src="${preparedSrc}"
                  width="${adjustedWidth}" 
                  height="${adjustedHeight}"
                  alt="${preparedAlt}"
                  style="width: 100%; height: 100%; position: absolute; top: 0; left: 0;"
                ></img>
              </div>
            </div>`;
      }

      return `<figure class="${figureClass}">
          ${imageHTML}
          ${captionHTML}
          </figure>`;
      break;
    }
    case "LINK":
    case "link": {
      const sanitizedUrl = sanitizeUrl(entity.data.href || entity.data.url);
      return `<a href="${sanitizedUrl}" target="_blank" rel="noopener noreferrer">${
        child || sanitizedUrl
      }</a>`;
      break;
    }
    case OEMBED_CONTENT_TYPE: {
      const {
        data: { html },
      } = entity;
      return `<div class="embed">${html}</div>`;
      break;
    }
    default:
      break;
  }
};

export const findScripts = (
  raw: RawDraftContentState | null | undefined,
): IScripts[] => {
  if (!raw) return [];
  const entities = Object.values(raw.entityMap || {});
  const providers = new Set<IScripts>();
  entities
    .filter((entity) => entity.type === OEMBED_CONTENT_TYPE)
    .forEach((entity) => {
      providers.add(entity.data.provider_name);
    });

  const arr: IScripts[] = [];
  providers.forEach((x) => arr.push(x));

  return arr;
};

export const convertToHTML = (
  raw: RawDraftContentState | null | undefined,
  limit?: number,
) => {
  try {
    if (!raw) return "";
    let content = raw;
    if (limit && raw) {
      content = convertToRaw(
        ContentState.createFromText(
          convertFromRaw(raw).getPlainText().slice(0, limit),
        ),
      );
    }
    return draftToHtml(content, undefined, false, customEntityTransform);
  } catch (err) {
    console.error("Invalid rich text format supplied", err);
    return "";
  }
};

const RichTextDisplay: React.FC<RichTextDisplayProps> = ({
  textJson,
  limit,
  id,
  loading = false,
}) => {
  const markup = useMemo(() => convertToHTML(textJson, limit), [
    textJson,
    limit,
  ]);

  const scriptContext = useContext(ScriptContext);
  useEffect(() => {
    if (!scriptContext) {
      // eslint-disable-next-line no-console
      console.warn(
        "RTD will not display social embeds due to missing ScriptContext",
      );
      return;
    }

    const { addOrReloadScript } = scriptContext;
    const scripts = findScripts(textJson);
    scripts.forEach((script) => {
      addOrReloadScript(script);
    });
  }, [scriptContext, textJson]);

  if (loading) {
    return <Loader active={loading} inline="centered" />;
  }

  return (
    <StyledRichTextHTML
      id={id}
      // Intended.
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
};
export default memo(RichTextDisplay);
